import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours at dusk as the first lantern is lit, in the violet twilight halfway
// through, and in the deep night of the festival's end.
const STOPS = {
  skyTop: ['#2A2766', '#1A1A4F', '#0B0D2E'],
  skyMiddle: ['#5E3F88', '#3A2B6E', '#1A1844'],
  skyLow: ['#C8688A', '#874A82', '#3D275C'],
  skyHorizon: ['#FFB27A', '#E8847A', '#874467'],
  farHill: ['#6C5390', '#46356F', '#211C48'],
  nearHill: ['#3A2D63', '#271F4F', '#121031'],
  waterTop: ['#9C6488', '#5E3B72', '#271D4B'],
  waterBottom: ['#1F1B4A', '#141238', '#07081E'],
  silhouette: ['#1A1436', '#140F2D', '#0A0920'],
} as const;

type ColorName = keyof typeof STOPS;

export type LanternsColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light over the lake as the evening goes on (0 at dusk, 1 at night). */
export const lanternsColors = (evening: number): LanternsColors => {
  const scaled = clamp(evening) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as LanternsColors,
  );
};

/** The sky is indigo at the top from dusk to night, so the header text stays white. */
export const lanternsChromeTone = (progress: number): ChromeTone =>
  luminance(lanternsColors(progress).skyTop) > 0.31 ? 'dark' : 'light';
